import { getAnonymousID, getSessionID } from "redline-client-sdk";

import { useUserStore } from "@libs/acp/store/user/useUserStore";
import {
	type ApiSigninResponse,
	handleRefreshToken,
	handleSignOut,
	isValidToken,
	shouldRefreshToken,
} from "./handleToken";

export class AbortRequestError extends Error {
	constructor() {
		super("Request aborted by handleBeforeRequest");
		this.name = "AbortRequestError";
	}
}

export type RequestContext = {
	url: string;
	headers: Record<string, string>;
};

/**
 * Mutates the axios request headers (auth, anonymous/session ids, content-type).
 * Throws {@link AbortRequestError} when the request must be aborted (invalid token).
 */
export async function handleBeforeRequest(context: RequestContext) {
	context.headers["Content-Type"] = "application/json;charset=UTF-8";
	context.headers["Access-Control-Allow-Origin"] = "*";
	context.headers["x-anonymous-id"] = getAnonymousID();
	context.headers["x-session-id"] = getSessionID();

	const isPublicResquest = context.url.includes("public");
	const isRefreshTokenRequest = context.url.includes("auth/refresh-token");
	const isLogoutRequest = context.url.includes("app/logout");
	const isEngageSignin = context.url.includes("engage/signin");

	if (!isEngageSignin && (!isPublicResquest || isRefreshTokenRequest)) {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		let token = useUserStore.getState().authenticatedUser.acp
			.auth as unknown as ApiSigninResponse | null;

		if (!token || !isValidToken(token)) {
			await handleSignOut();
			throw new AbortRequestError();
		}

		if (isRefreshTokenRequest) {
			context.headers["Refresh-Token"] = token?.refresh_token;
			context.headers["Authorization"] = `Bearer ${token?.access_token}`;
		}

		if (!isPublicResquest) {
			if (shouldRefreshToken(token)) {
				token = await handleRefreshToken();
			}

			if (token?.access_token) {
				context.headers.token = token.access_token;
				context.headers["X-Auth-Token"] = token.access_token;
				context.headers["Authorization"] = `Bearer ${token.access_token}`;
			}

			if (isLogoutRequest && token?.refresh_token) {
				context.headers["Refresh-Token"] = token.refresh_token;
			}
		}
	}
}
